// src/components/DrawingCanvas/DrawingCanvas.jsx
//
// Ana canvas component'i — hook orkestrasyonu ve JSX.
// Agir mantik hooks/canvas/ ve utils/ altina cikarildi.

import React, { useRef, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Callout proje notları (salt-okunur ön izleme modunda)
import useCalloutNotes from '../../hooks/notes/useCalloutNotes';
import { CalloutNoteButtons, CalloutNoteOverlay } from '../notes/CalloutNoteUI';
import { computeSceneBaseHeight } from '../../utils/notes/calloutRender';

// Canvas hooks (extracted)
import {
  useThreeSetup, useTabSwitch, useDxfScene, useLayerSync,
  useCustomCursor, useTypeMenus,
} from '../../modules/canvas';
import TypeMenuOverlay from './TypeMenuOverlay';

// Drawing hooks
import {
  useDrawLine, useDrawPolyline, useDrawSpline, useDrawCircle, useDrawArc,
  useDrawEllipse, useDrawRect, useDrawText, useDrawMtext as useDrawMText,
  useDrawCallout,
} from '../../modules/drawing';
import {
  useSocket, useLighting, useFixture, useCableShaft,
  useMachines as useDrawMachine, useDoorOpener,
  useFixtureDraggableText, useBuatTypeSync,
} from '../../modules/tesisat';
import useCalloutSync from '../../hooks/useCalloutSync';

// Core hooks
import { useSelection } from '../../modules/selection';
import { useWireBundling, useWireLabelMenu, useWireSplitMenu, useFloorTransitionContinue, useWireMarkerMove } from '../../modules/wiring';
import { useGizmoManager, useGizmoTitle } from '../../modules/gizmo';
import { useTestScene } from '../../hooks/useTestScene';
import useSnapPoints from '../../shared/snap/useSnapPoints';
import useErase from '../../hooks/useErase';
import useModifyMove from '../../hooks/useModifyMove';
import useModifyCopy from '../../hooks/useModifyCopy';
import useModifyRotate from '../../hooks/useModifyRotate';

// Component hooks
import { useTesisatRebuild } from '../../modules/persistence';
import { useTesisatMeshSync, useSceneTesisatSync } from '../../modules/fpSync';
import { useWireRebuild } from '../../modules/persistence';
import useGrounding from '../../hooks/grounding/useGrounding';

// Layout & Electrical
import './DrawingCanvas.css';
import { useElectricalLayout } from '../../hooks/layout/useElectricalLayout';
import { useDocumentMove } from '../../modules/documents';
import { useLowCurrentLayout } from '../../hooks/layout/useLowCurrentLayout';
import { useFireDetectionLayout } from '../../hooks/layout/useFireDetectionLayout';
import { useConstructionSiteLayout } from '../../hooks/layout/useConstructionSiteLayout';
import { useConnectionLineLayout } from '../../hooks/layout/useConnectionLineLayout';
import { resolveTabId } from '../../redux/projectTabsSlice';
import { useDocumentDragging } from '../../hooks/layout/useDocumentDragging';
import { useSupplyBoxDrawing } from '../../modules/wiring';
import { useSupplyPoint } from '../../modules/panel';
import { useMainPanel } from '../../modules/panel';
import { useElectricalPanel } from '../../modules/panel';
import useBell from '../../hooks/LowCurrent/useBell';
import useBellButton from '../../hooks/LowCurrent/useBellButton';
import useComponentVisualUpdater from '../../hooks/visual/useComponentVisualUpdater';
import useMPanelGrounding from '../../hooks/grounding/useMPanelGrounding';
import useEntitiesShow from '../../hooks/useEntitiesShow';
import EntitiesPopup from '../layout/EntitiesPopup';
import { useImagePaste } from '../../hooks/useImagePaste';
import MemoryMonitor from '../dev/MemoryMonitor';

// Undo/Redo
import {
  useGlobalUndoRedo, useUndo, useRedo, ChronoUndoRedoManager,
} from '../../modules/undoRedo';

const DrawingCanvas = ({ scenesRef, onSceneReady, preview = false, previewProjectId = null }) => {
  const canvasRef = useRef();
  const dispatch = useDispatch();

  // --- THREE.js setup ---
  const { threeState, setThreeState, activeSceneRef, controlsRef } = useThreeSetup(canvasRef, scenesRef, onSceneReady);
  const scene = threeState?.scene;
  const camera = threeState?.camera;
  const renderer = threeState?.renderer;

  // --- Tab switch ---
  const { activeTabId } = useTabSwitch(threeState, setThreeState, scenesRef, activeSceneRef, controlsRef, canvasRef, onSceneReady);

  // --- Callout proje notları (YALNIZCA salt-okunur ön izleme modunda) ---
  // Not tabId'si JbzViewer konvansiyonuyla (legacy: elektrik/mimari...) saklanır;
  // editörün activeTabId'si backend code (strong_current) olabilir → çevir.
  const notesLegacyTab = useMemo(() => {
    const CODE_TO_LEGACY = {
      strong_current: 'elektrik', weak_current: 'telekom', fire_detection: 'yangin',
      construction_site: 'santiye', connection_line: 'baglanti',
    };
    return CODE_TO_LEGACY[activeTabId] || activeTabId;
  }, [activeTabId]);
  const notesBaseHeight = useMemo(
    () => (preview && scene ? computeSceneBaseHeight(scene) : 0),
    [preview, scene, activeTabId], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const getThreeForNotes = useCallback(
    () => (scene && camera && renderer ? { scene, camera, renderer } : null),
    [scene, camera, renderer],
  );
  const calloutNotes = useCalloutNotes({
    getThree: getThreeForNotes,
    ready: !!(scene && camera && renderer),
    containerRef: canvasRef,
    projectId: previewProjectId,
    activeTab: notesLegacyTab,
    baseHeight: notesBaseHeight,
    enabled: !!(preview && previewProjectId),
  });

  // --- DXF scene ---
  const { dxfEntities } = useDxfScene(threeState, scenesRef, controlsRef, canvasRef, onSceneReady);

  // --- Layer sync ---
  useLayerSync(scene);

  // --- Custom cursor ---
  const { cursorXRef, cursorYRef, cursorBoxRef } = useCustomCursor(canvasRef);

  // --- Snap points ---
  const { snapPoints, refreshSnapPoints } = useSnapPoints(scene);

  // --- Undo/Redo ---
  const undoRedoManagerRef = useRef(new ChronoUndoRedoManager(dispatch));
  useEffect(() => {
    if (typeof window !== 'undefined') window.undoRedoManager = undoRedoManagerRef.current;
    return () => { if (typeof window !== 'undefined') window.undoRedoManager = null; };
  }, []);
  useUndo(scene, { undoRedoManagerRef });
  useRedo(scene, { undoRedoManagerRef });
  useGlobalUndoRedo(scene, camera, renderer, undoRedoManagerRef);

  // --- Tab ID resolution ---
  const projectTabs = useSelector(state => state.projectTabs.tabs);
  const resolvedTabs = useMemo(() => ({
    elektrik: resolveTabId(projectTabs, 'elektrik'),
    telekom: resolveTabId(projectTabs, 'telekom'),
    yangin: resolveTabId(projectTabs, 'yangin'),
    santiye: resolveTabId(projectTabs, 'santiye'),
    baglanti: resolveTabId(projectTabs, 'baglanti'),
  }), [projectTabs]);

  // --- Layout hooks ---
  // [SAVE-LOAD-REWRITE] Hook'lar AKTİF — drawing-time'da gerekli.
  // Snapshot mode (2sn pencere) içinde her hook kendi guard'ı ile skip eder
  // → restore'da duplicate üretmez, drawing-time'da normal layout güncellemesi.
  const { allGroupsRef: electricalGroupsRef, boundsRef: electricalBoundsRef, layoutStateRef: electricalLayoutStateRef } =
    useElectricalLayout(scenesRef, resolvedTabs.elektrik);
  // Ctrl+M ile doc M-drag özelliği (elektrik paftası için)
  useDocumentMove(scene, camera, renderer, electricalLayoutStateRef);
  useLowCurrentLayout(scenesRef, resolvedTabs.telekom);
  useFireDetectionLayout(scenesRef, resolvedTabs.yangin);
  useConstructionSiteLayout(scenesRef, resolvedTabs.santiye, { camera, controlsRef, canvasRef });
  useConnectionLineLayout(scenesRef, resolvedTabs.baglanti);

  // --- Type menus ---
  const { menus, handleDrag, handleClose, selectedObjectIds } = useTypeMenus(scene, camera, renderer);

  // --- Wire titles (for gizmo dragging) ---
  const wireTitles = useMemo(() => {
    const titles = [];
    if (!scene) return titles;
    scene.traverse(child => {
      if (child.userData?.type === 'wireTitle' && selectedObjectIds.includes(child.userData.id)) titles.push(child);
      if (child.userData?.isTitle && child.userData?.titleType === 'panel' && child.userData?.isDraggable) titles.push(child);
      if (child.userData?.type === 'wireLabel') titles.push(child);
    });
    return titles;
  }, [scene, selectedObjectIds]);

  // --- Drawing commands ---
  useDrawLine(scene, camera, renderer, snapPoints);
  useDrawPolyline(scene, camera, renderer, snapPoints);
  useDrawSpline(scene, camera, renderer, snapPoints);
  useDrawCircle(scene, camera, renderer, snapPoints);
  useDrawArc(scene, camera, renderer, snapPoints);
  useDrawEllipse(scene, camera, renderer, snapPoints);
  useDrawRect(scene, camera, renderer, snapPoints);
  useCableShaft(scene, camera, renderer, snapPoints);
  const textInput = useDrawText(scene, camera, renderer, snapPoints);
  const mTextInput = useDrawMText(scene, camera, renderer, snapPoints);
  const calloutInput = useDrawCallout(scene, camera, renderer, snapPoints);
  useCalloutSync(scene, camera);

  // --- Component visual updater ---
  useComponentVisualUpdater(scene, camera, renderer);

  // --- Document dragging ---
  useDocumentDragging(
    electricalGroupsRef?.current ?? [],
    camera, renderer?.domElement,
    electricalBoundsRef?.current ?? {}
  );

  // --- Selection & Gizmos ---
  useSelection(scene, camera, renderer);
  useGizmoManager(scene, camera, renderer);
  useTestScene(scene, camera, renderer?.domElement);
  useGizmoTitle(scene, camera, renderer, wireTitles);

  // --- Modify commands ---
  useErase(scene);
  useModifyMove(scene, camera, renderer, refreshSnapPoints);
  useModifyCopy(scene, camera, renderer, refreshSnapPoints);
  useModifyRotate(scene, camera, renderer, refreshSnapPoints);

  // --- Electrical commands ---
  useSupplyBoxDrawing(scene, camera, renderer, undoRedoManagerRef.current);
  useSupplyPoint(scene);
  useMainPanel(scene, camera, renderer, undoRedoManagerRef);
  useElectricalPanel(scene, camera, renderer, undoRedoManagerRef);
  useLighting(scene, camera, renderer, undoRedoManagerRef);
  useFixture(scene, camera, renderer, undoRedoManagerRef);
  useFixtureDraggableText(scene, camera, renderer);
  useBuatTypeSync(scene);
  useDrawMachine(scene, camera, renderer, undoRedoManagerRef);
  useSocket(scene, camera, renderer, undoRedoManagerRef);

  // --- Tesisat & Wire rebuild ---
  // [SAVE-LOAD-REWRITE] AKTİF — drawing-time için gerekli. Snapshot mode
  // (2sn pencere) içinde her hook kendi guard'ı ile skip eder.
  useTesisatRebuild(scenesRef, resolvedTabs.elektrik);
  useTesisatMeshSync(scenesRef, resolvedTabs.elektrik);
  useSceneTesisatSync(scenesRef, resolvedTabs.elektrik);
  useWireRebuild(scenesRef, resolvedTabs.elektrik);

  // --- Bell system ---
  useBell(scene, camera, renderer, undoRedoManagerRef);
  useBellButton(scene, camera, renderer);
  useDoorOpener(scene, camera, renderer);

  // --- Grounding ---
  const lightingSnapPoints = useMemo(() => {
    return snapPoints.filter(p => p.type === 'buatLightingCircle' || p.type === 'buatLightingSquare');
  }, [snapPoints]);
  const targetPlanes = useMemo(() => {
    const planes = [];
    if (!scene) return planes;
    scene.traverse((obj) => {
      if ((obj.isLine || obj.isMesh || obj.isLineLoop || obj.isLineSegments) && obj.geometry && obj.visible !== false) {
        planes.push(obj);
      }
    });
    return planes;
  }, [scene, dxfEntities]);
  useMPanelGrounding(scene, camera, renderer, snapPoints, targetPlanes);
  useGrounding(scene, camera, renderer, snapPoints, targetPlanes);

  // --- Wire bundling & labels ---
  useWireBundling(scene, camera, renderer);
  // Kat-geçiş işaretçisinden devam (gelen ok'a tıklayınca hat başlat)
  useFloorTransitionContinue(scene, camera, renderer);
  // wireMarker'ları M tuşu ile sürükle (üst üste binmeyi elle ayır)
  useWireMarkerMove(scene, camera, renderer?.domElement);
  const wireLabelMenuPortal = useWireLabelMenu(scene, camera, renderer);
  const wireSplitMenuPortal = useWireSplitMenu(scene, camera, renderer);

  // --- Entities popup ---
  const entitiesPopupState = useEntitiesShow(dxfEntities);

  // --- Image paste ---
  useImagePaste(scene, camera, renderer, { layerName: 'IMAGES', defaultWidth: 50 });

  // --- Render ---
  return (
    <div className="drawing-canvas-container" ref={canvasRef}>
      {textInput}
      {mTextInput}
      {calloutInput}

      {/* Salt-okunur ön izleme: callout proje-notu menüsü + overlay */}
      {preview && previewProjectId && (
        <>
          <div className="dxf-viewer-controls" style={{ position: 'absolute', top: 10, right: 10, zIndex: 30 }}>
            <CalloutNoteButtons {...calloutNotes} />
          </div>
          <CalloutNoteOverlay {...calloutNotes} />
        </>
      )}

      {entitiesPopupState.showPopup && (
        <EntitiesPopup entities={entitiesPopupState.entities} onClose={entitiesPopupState.closePopup} />
      )}

      <TypeMenuOverlay menus={menus} handleDrag={handleDrag} handleClose={handleClose} scene={scene} />

      <div ref={cursorXRef} className="cursor-line-x"></div>
      <div ref={cursorYRef} className="cursor-line-y"></div>
      <div ref={cursorBoxRef} className="cursor-box"></div>

      {wireLabelMenuPortal}
      {wireSplitMenuPortal}

      <MemoryMonitor renderer={renderer} />
    </div>
  );
};

export default DrawingCanvas;
